import { Component, computed, output, signal } from '@angular/core';
import { CurrencyPipe } from '@angular/common';

/** One open on-site option the admin can send to the customer. */
export interface CustomerOptionChoice {
  /** The vendor estimate (option) key. */
  estimateKey: string;
  /** Option label ("Option 1", or the vendor's title). */
  label: string;
  /** The vendor's total for the option. */
  total: number;
}

/**
 * "Select options to send to the customer": shown before a customer estimate is created from an on-site approval
 * whose vendor sent 2+ options that are still open. One checkbox per open option (all ticked to start, at least
 * one required). Choosing 1 goes on as an ordinary single customer estimate; 2+ become one multi-option customer
 * estimate. Nothing is declined: options that are not sent stay open until the customer decides.
 *
 * Shared by the live-chat pop-up (Get Customer Approval) and the Estimates page (Create Customer Estimate); the
 * host calls {@link open} and listens for {@link selected}.
 */
@Component({
  selector: 'app-customer-option-select-modal',
  standalone: true,
  imports: [CurrencyPipe],
  templateUrl: './customer-option-select-modal.component.html',
  styleUrls: ['./customer-option-select-modal.component.scss'],
})
export class CustomerOptionSelectModalComponent {
  /** The ticked options' keys, in option order, when the admin presses Continue. */
  readonly selected = output<string[]>();
  /** Cancel: nothing was chosen. */
  readonly cancelled = output<void>();

  readonly isOpen = signal(false);
  readonly choices = signal<CustomerOptionChoice[]>([]);
  private readonly tickedKeys = signal<ReadonlySet<string>>(new Set());

  readonly tickedCount = computed(() => this.choices().filter((c) => this.tickedKeys().has(c.estimateKey)).length);
  readonly canContinue = computed(() => this.tickedCount() > 0);

  /** Opens the step with every option ticked. */
  open(choices: CustomerOptionChoice[]): void {
    this.choices.set(choices);
    this.tickedKeys.set(new Set(choices.map((c) => c.estimateKey)));
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  isTicked(key: string): boolean {
    return this.tickedKeys().has(key);
  }

  toggle(key: string): void {
    this.tickedKeys.update((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  cancel(): void {
    if (!this.isOpen()) return;
    this.close();
    this.cancelled.emit();
  }

  continue(): void {
    if (!this.canContinue()) return;
    const keys = this.choices()
      .filter((c) => this.tickedKeys().has(c.estimateKey))
      .map((c) => c.estimateKey);
    this.close();
    this.selected.emit(keys);
  }
}
